/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Account Deletion Confirmation Page
 * Handles the email confirmation link for account deletion
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'

export default function ConfirmDeletion() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('loading') // loading, confirm, success, error
  const [error, setError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const token = searchParams.get('token')

  useEffect(() => {
    // Validate token exists
    if (!token) {
      setStatus('error')
      setError('Invalid or missing deletion token')
    } else {
      setStatus('confirm')
    }
  }, [token])

  const handleConfirmDeletion = async () => {
    try {
      setIsDeleting(true)
      const response = await axios.post('/api/compliance/confirm-deletion', { token })

      if (response.data.success) {
        setStatus('success')
        toast.success('Account deleted successfully')

        // Redirect to homepage after 5 seconds
        setTimeout(() => {
          window.location.href = '/'
        }, 5000)
      }
    } catch (err) {
      setStatus('error')
      setError(err.response?.data?.error || 'Failed to delete account')
      toast.error(err.response?.data?.error || 'Failed to delete account')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancel = () => {
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-primary-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Validating...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we validate your deletion request.
              </p>
            </div>
          )}

          {/* Confirmation State */}
          {status === 'confirm' && (
            <div>
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-4">
                Confirm Account Deletion
              </h2>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">
                  ⚠️ Warning: This Action is Permanent
                </h3>
                <p className="text-red-800 dark:text-red-300 text-sm mb-3">
                  You are about to permanently delete your Deaveris account. This action cannot be
                  undone.
                </p>
                <div className="space-y-2">
                  <p className="text-red-900 dark:text-red-200 text-sm font-medium">
                    The following will be deleted:
                  </p>
                  <ul className="list-disc list-inside text-red-800 dark:text-red-300 text-sm space-y-1 ml-2">
                    <li>Your profile information and credentials</li>
                    <li>All challenge sessions and responses</li>
                    <li>Progress data and achievements</li>
                    <li>Skill levels and strategy progress</li>
                    <li>All feedback and history records</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleConfirmDeletion}
                  disabled={isDeleting}
                  className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Deleting Account...
                    </>
                  ) : (
                    'Yes, Delete My Account Permanently'
                  )}
                </button>

                <button
                  onClick={handleCancel}
                  disabled={isDeleting}
                  className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Cancel - Keep My Account
                </button>
              </div>

              <p className="text-gray-500 dark:text-gray-400 text-sm text-center mt-6">
                This confirmation link will expire in 24 hours from the time you requested deletion.
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Account Deleted Successfully
              </h2>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your account and all associated data have been permanently deleted from our systems.
              </p>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  You will be redirected to the homepage in a few seconds. If you would like to use
                  Deaveris again in the future, you're welcome to create a new account.
                </p>
              </div>

              <button
                onClick={() => (window.location.href = '/')}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
              >
                Go to Homepage
              </button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Deletion Failed
              </h2>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {error || 'Unable to process your account deletion request.'}
              </p>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                  Common Issues:
                </h3>
                <ul className="list-disc list-inside text-yellow-800 dark:text-yellow-300 text-sm space-y-1">
                  <li>The deletion link may have expired (24 hour limit)</li>
                  <li>The link may have already been used</li>
                  <li>The link may be invalid or corrupted</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => navigate('/profile#privacy')}
                  className="w-full px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                  Go to Account Settings
                </button>

                <button
                  onClick={() => navigate('/')}
                  className="w-full px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          Deaveris - Secure Account Management
        </p>
      </motion.div>
    </div>
  )
}
